import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Map, ExternalLink } from "lucide-react";

export default function LotMaps() {
  const [maps, setMaps] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/lot-maps")
      .then((r) => { if (alive) setMaps(r.data || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const lower = q.trim().toLowerCase();
  const filtered = lower
    ? maps.filter((m) =>
        [m.label, m.lot_number, m.plan_name, m.plan_drawing_number, m.lot_itp_number, m.markup_type]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(lower)
      )
    : maps;

  return (
    <div className="p-4 md:p-8" data-testid="lot-maps-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="overline text-teal-700">Lot Maps</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
            Lot boundary markups
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Each lot map references the plan revision it was drawn against. Source plans are managed in the
            <Link to="/plans" className="ml-1 font-semibold text-teal-700 hover:underline">Plans</Link> tab.
          </p>
        </div>
        <Input
          data-testid="lot-maps-search"
          placeholder="Search lot, plan, drawing..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 w-72"
        />
      </div>

      <Card className="mt-6 overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Lot</th>
                <th className="px-4 py-3">ITP</th>
                <th className="px-4 py-3">Source plan</th>
                <th className="px-4 py-3">Plan revision used</th>
                <th className="px-4 py-3">Plan current revision</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Last updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500" data-testid="lot-maps-empty">
                  <Map className="mx-auto mb-2 size-8 text-slate-300" />
                  No lot maps yet. Create a lot, open its source plan from the Plans tab and draw a boundary.
                </td></tr>
              )}
              {filtered.map((m) => {
                const stale = m.plan_revision_used && m.plan_current_revision && m.plan_revision_used !== m.plan_current_revision;
                return (
                  <tr key={m.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`lot-map-row-${m.id}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">
                      <Link to={`/lots/${m.linked_lot_id}`} className="text-teal-700 hover:underline">{m.lot_number || m.linked_lot_id?.slice(0, 8)}</Link>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {m.lot_itp_number ? <span className="font-mono">{m.lot_itp_number}</span> : <span className="text-slate-400">—</span>}
                      {m.lot_itp_name && <div className="text-xs text-slate-500">{m.lot_itp_name}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/plans/${m.plan_id}`} className="font-semibold text-slate-900 hover:underline">{m.plan_name || "—"}</Link>
                      {m.plan_drawing_number && <div className="text-xs text-slate-500 font-mono">{m.plan_drawing_number}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono">{m.plan_revision_used || "—"}</td>
                    <td className="px-4 py-3 font-mono">
                      {m.plan_current_revision || "—"}
                      {stale && <Badge variant="outline" className="ml-2 border-amber-300 bg-amber-50 text-amber-800" data-testid="stale-revision-badge">Newer revision</Badge>}
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-700">{m.markup_type || "boundary"}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</div>
                      <div className="text-xs text-slate-500">{m.created_by_name || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{m.updated_at ? new Date(m.updated_at).toLocaleDateString() : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3">
                      <Link to={`/plans/${m.plan_id}?markup=${m.id}`} className="inline-flex items-center gap-1 text-teal-700 hover:underline" data-testid={`open-lot-map-${m.id}`}>
                        Open <ExternalLink className="size-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
